import { useState, useEffect, useRef } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const API_URL = process.env.REACT_APP_API_URL;
const INSTRUCTOR_CHAT_FOLDER_ID = '1Ku3NJvmHZhdImpqNH53RhEl5FPBs95AV';

const EMOJI_LIST = [
  '😀','😁','😂','🤣','😃','😄','😅','😆','😉','😊',
  '😋','😎','😍','🥰','😘','😗','😙','😚','🙂','🤗',
  '🤩','🤔','🤨','😐','😑','😶','🙄','😏','😣','😥',
  '😮','🤐','😯','😪','😫','🥱','😴','😌','😛','😜',
  '😝','🤤','😒','😓','😔','😕','🙃','🤑','😲','☹️',
  '🙁','😖','😞','😟','😤','😢','😭','😦','😧','😨',
  '😩','🤯','😬','😰','😱','🥵','🥶','😳','🤪','😵',
  '😡','😠','🤬','😷','🤒','🤕','🤢','🤮','🤧','🥴',
  '👍','👎','👌','✌️','🤞','🤟','🤘','👈','👉','👆',
  '👇','☝️','✋','🤚','🖐','🖖','👋','🤙','💪','🦾',
  '🙌','👏','🤲','🤝','🙏','❤️','🧡','💛','💚','💙',
  '💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗',
  '💖','💘','💝','💟','☮️','✝️','☯️','🔥','💫','⭐',
  '🌟','✨','⚡','🎉','🎊','🎈','🎁','🏆','🥇','💯'
];

function apiHeaders(extra = {}) {
  return {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}

function getDriveFileId(url) {
  if (!url) return null;
  const match = url.match(/[?&]id=([^&]+)/) || url.match(/\/d\/([^/?]+)/);
  return match ? match[1] : null;
}

function isImageFile(fileType, fileName) {
  if (fileType === 'image') return true;
  if (!fileName) return false;
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
}

async function fetchChats(institutionId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructor_chats?or=(type.eq.global,institution_id.eq.${institutionId})&select=*&order=type.asc`,
    { headers: apiHeaders() }
  );
  return response.json();
}

async function fetchMessages(chatId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructor_chat_messages?chat_id=eq.${chatId}&order=created_at.asc&select=*,instructors(id,name,email)`,
    { headers: apiHeaders() }
  );
  return response.json();
}

async function sendMessage(chatId, instructorId, content, fileUrl, fileName, fileType, replyToMessageId = null) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructor_chat_messages`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }),
      body: JSON.stringify({
        chat_id: chatId,
        instructor_id: instructorId,
        content: content || null,
        file_url: fileUrl || null,
        file_name: fileName || null,
        file_type: fileType || null,
        reply_to_message_id: replyToMessageId || null
      })
    }
  );
  const data = await response.json();
  return data?.[0] || null;
}

async function deleteMessage(messageId, instructorId) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/instructor_chat_messages?id=eq.${messageId}&instructor_id=eq.${instructorId}`,
    {
      method: 'PATCH',
      headers: apiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ is_deleted: true, content: null, file_url: null, file_name: null })
    }
  );
}

async function markRead(messageIds, instructorId) {
  if (!messageIds.length) return;
  await fetch(
    `${SUPABASE_URL}/rest/v1/instructor_chat_message_reads`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates'
      }),
      body: JSON.stringify(messageIds.map(id => ({
        message_id: id,
        instructor_id: instructorId
      })))
    }
  );
}

async function fetchReadCounts(messageIds) {
  if (!messageIds.length) return {};
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructor_chat_message_reads?message_id=in.(${messageIds.join(',')})&select=message_id`,
    { headers: apiHeaders() }
  );
  const data = await response.json();
  const counts = {};
  data.forEach(row => {
    counts[row.message_id] = (counts[row.message_id] || 0) + 1;
  });
  return counts;
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) +
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function getInstructorName(instructor) {
  return instructor?.name || instructor?.email || 'Unknown';
}

function getMessagePreview(message) {
  if (!message) return 'Original message unavailable';
  if (message.is_deleted) return 'Message removed';
  const content = String(message.content || '').trim();
  if (content) return content.length > 120 ? content.slice(0, 117) + '...' : content;
  if (message.file_url && isImageFile(message.file_type, message.file_name))
    return 'Image: ' + (message.file_name || 'Image');
  if (message.file_url) return 'File: ' + (message.file_name || 'File');
  return 'Message';
}

export default function InstructorChat({ session }) {
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [readCounts, setReadCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [error, setError] = useState('');
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [replyToMessageId, setReplyToMessageId] = useState(null);

  const messagesEndRef = useRef(null);
  const textInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!session.institutionId) return;
    fetchChats(session.institutionId)
      .then(data => {
        const loaded = Array.isArray(data) ? data : [];
        setChats(loaded);
        if (loaded.length) setActiveChatId(loaded[0].id);
      })
      .catch(() => setError('Failed to load chats.'))
      .finally(() => setLoading(false));
  }, [session.institutionId]);

  useEffect(() => {
    if (!activeChatId) return;
    setMessages([]);
    setReadCounts({});
    setReplyToMessageId(null);

    fetchMessages(activeChatId)
      .then(async msgs => {
        const loaded = Array.isArray(msgs) ? msgs : [];
        setMessages(loaded);
        const ids = loaded.map(m => m.id);
        const counts = await fetchReadCounts(ids);
        setReadCounts(counts);
        const unread = loaded
          .filter(m => m.instructor_id !== session.instructorId)
          .map(m => m.id);
        if (unread.length) await markRead(unread, session.instructorId);
      })
      .catch(() => setError('Failed to load messages.'));
  }, [activeChatId, session.instructorId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function handleSend(e) {
    e?.preventDefault();
    if (!text.trim() || sending || !activeChatId) return;
    setSending(true);
    setError('');
    const messageText = text.trim();
    try {
      const created = await sendMessage(
        activeChatId, session.instructorId,
        messageText, null, null, null, replyToMessageId
      );
      if (!created) throw new Error('Message not created.');
      setMessages(prev => {
        if (prev.some(m => m.id === created.id)) return prev;
        return [...prev, { ...created, instructors: { id: session.instructorId, name: session.instructorName, email: session.email } }];
      });
      setText('');
      setReplyToMessageId(null);
      setShowEmoji(false);
    } catch (err) {
      setError(err.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  }

  async function handleFileUpload(file) {
    if (!file || !activeChatId) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }
    setUploadingFile(true);
    setError('');
    try {
      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
      const created = await sendMessage(
        activeChatId, session.instructorId,
        null, 'pending', file.name,
        isImage ? 'image' : 'document', replyToMessageId
      );
      if (!created) throw new Error('Message record not created.');

      setMessages(prev => {
        if (prev.some(m => m.id === created.id)) return prev;
        return [...prev, { ...created, instructors: { id: session.instructorId, name: session.instructorName, email: session.email } }];
      });
      setReplyToMessageId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadingFile(false);

      // Upload to Drive
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result.split(',')[1];
        await fetch(API_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'uploadChatFile',
            base64Data,
            mimeType: file.type || 'application/octet-stream',
            fileName: file.name,
            subFolder: INSTRUCTOR_CHAT_FOLDER_ID,
            messageId: created.id,
            tableTarget: 'instructor_chat_messages'
          })
        });
      };
      reader.readAsDataURL(file);

      // Poll for real URL
      for (let i = 0; i < 15; i++) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const checkResponse = await fetch(
          `${SUPABASE_URL}/rest/v1/instructor_chat_messages?id=eq.${created.id}&select=file_url`,
          { headers: apiHeaders() }
        );
        const checkData = await checkResponse.json();
        if (checkData[0]?.file_url && checkData[0].file_url !== 'pending') {
          setMessages(prev => prev.map(m =>
            m.id === created.id ? { ...m, file_url: checkData[0].file_url } : m
          ));
          break;
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to upload file.');
      setUploadingFile(false);
    }
  }

  async function handleUnsend(messageId) {
    const target = messages.find(m => m.id === messageId);
    if (!target || target.instructor_id !== session.instructorId) return;
    if (!window.confirm('Remove this message?')) return;
    try {
      await deleteMessage(messageId, session.instructorId);
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, is_deleted: true, content: null, file_url: null, file_name: null } : m
      ));
    } catch (err) {
      setError(err.message || 'Failed to remove message.');
    }
  }

  function handleReply(message) {
    if (!message || message.is_deleted) return;
    setReplyToMessageId(message.id);
    window.requestAnimationFrame(() => textInputRef.current?.focus());
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const replyingToMessage = replyToMessageId
    ? messages.find(m => m.id === replyToMessageId) || null
    : null;

  if (loading) return <div style={styles.muted}>Loading...</div>;

  return (
    <div style={styles.wrap}>
      <h1 style={styles.heading}>Instructor Chat</h1>

      <div style={styles.shell}>
        <div style={styles.chatHeader}>
          <select
            style={styles.chatSelect}
            value={activeChatId || ''}
            onChange={e => setActiveChatId(e.target.value)}
          >
            {chats.map(chat => (
              <option key={chat.id} value={chat.id}>
                {chat.type === 'global' ? '🌐 Global' : `🏛 ${chat.name}`}
              </option>
            ))}
          </select>

          <div style={styles.headerTools}>
            <button
              type="button"
              style={{ ...styles.iconBtn, ...(showSearch ? styles.iconBtnActive : {}) }}
              onClick={() => { setShowSearch(p => !p); setSearchTerm(''); }}
            >Search</button>
            <button
              type="button"
              style={{ ...styles.iconBtn, ...(showFileBrowser ? styles.iconBtnActive : {}) }}
              onClick={() => setShowFileBrowser(p => !p)}
            >Files</button>
          </div>
        </div>

        {showSearch && (
          <div style={styles.searchBar}>
            <input
              autoFocus
              style={styles.searchInput}
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search messages..."
            />
            {searchTerm && (
              <button style={styles.searchClear} onClick={() => setSearchTerm('')}>✕</button>
            )}
          </div>
        )}

        {showFileBrowser && (
          <div style={styles.fileBrowser}>
            <div style={styles.fileBrowserHeader}>
              <span style={styles.fileBrowserTitle}>Files</span>
              <button style={styles.fileBrowserClose} onClick={() => setShowFileBrowser(false)}>✕</button>
            </div>
            <div style={styles.fileBrowserList}>
              {messages
                .filter(m => m.file_url && m.file_url !== 'pending' && m.file_type !== 'image')
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map(m => (
                  <div key={m.id} style={styles.fileBrowserItem}>
                    <div style={styles.fileBrowserName}>{m.file_name || 'File'}</div>
                    <div style={styles.fileBrowserDate}>
                      {new Date(m.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                    <div style={styles.fileBrowserActions}>
                      <a href={`https://drive.google.com/file/d/${getDriveFileId(m.file_url)}/preview`} target="_blank" rel="noreferrer" style={styles.fileBrowserBtn}>View</a>
                      <a href={`https://drive.google.com/uc?export=download&id=${getDriveFileId(m.file_url)}`} target="_blank" rel="noreferrer" style={styles.fileBrowserBtn}>Download</a>
                    </div>
                  </div>
                ))}
              {messages.filter(m => m.file_url && m.file_url !== 'pending' && m.file_type !== 'image').length === 0 && (
                <div style={styles.fileBrowserEmpty}>No files uploaded yet.</div>
              )}
            </div>
          </div>
        )}

        <div style={styles.messagesArea}>
          {messages
            .filter(msg =>
              !searchTerm.trim() ||
              (msg.content && msg.content.toLowerCase().includes(searchTerm.toLowerCase())) ||
              (msg.file_name && msg.file_name.toLowerCase().includes(searchTerm.toLowerCase()))
            )
            .map(msg => {
              const isOwn = msg.instructor_id === session.instructorId;
              const isDeleted = msg.is_deleted;
              const readCount = readCounts[msg.id] || 0;
              const isHovered = hoveredMessageId === msg.id;
              const repliedToMessage = msg.reply_to_message_id
                ? messages.find(m => m.id === msg.reply_to_message_id) || null
                : null;

              return (
                <div
                  key={msg.id}
                  id={'chat-message-' + msg.id}
                  style={{ ...styles.messageRow, ...(isOwn ? styles.messageRowOwn : {}) }}
                  onMouseEnter={() => setHoveredMessageId(msg.id)}
                  onMouseLeave={() => setHoveredMessageId(null)}
                >
                  {!isOwn && (
                    <div style={styles.senderName}>
                      {getInstructorName(msg.instructors)}
                    </div>
                  )}

                  <div style={{ ...styles.bubble, ...(isOwn ? styles.bubbleOwn : styles.bubbleOther) }}>
                    {isDeleted ? (
                      <div style={styles.deletedText}>Message removed</div>
                    ) : (
                      <>
                        {msg.reply_to_message_id && (
                          <div style={styles.replyQuote}>
                            <div style={styles.replyQuoteAuthor}>
                              {repliedToMessage
                                ? repliedToMessage.instructor_id === session.instructorId ? 'You' : getInstructorName(repliedToMessage.instructors)
                                : 'Original message'}
                            </div>
                            <div style={styles.replyQuoteText}>{getMessagePreview(repliedToMessage)}</div>
                          </div>
                        )}

                        {msg.content && <div style={styles.messageText}>{msg.content}</div>}

                        {msg.file_url && msg.file_url !== 'pending' && isImageFile(msg.file_type, msg.file_name) && (
                          <div>
                            <img
                              src={`https://drive.google.com/thumbnail?id=${getDriveFileId(msg.file_url)}&sz=w800`}
                              alt={msg.file_name || 'Image'}
                              style={styles.messageImage}
                              onClick={() => window.open(msg.file_url, '_blank')}
                            />
                            <div style={styles.attachmentActions}>
                              <a href={`https://drive.google.com/uc?export=download&id=${getDriveFileId(msg.file_url)}`} target="_blank" rel="noreferrer" style={styles.attachmentButton}>Download</a>
                            </div>
                          </div>
                        )}

                        {msg.file_url && msg.file_url !== 'pending' && !isImageFile(msg.file_type, msg.file_name) && (
                          <div style={styles.fileAttachment}>
                            <div style={styles.fileHeader}>
                              <span style={styles.fileIcon}>FILE</span>
                              <div style={styles.fileName}>{msg.file_name || 'File'}</div>
                            </div>
                            <div style={styles.attachmentActions}>
                              <a href={`https://drive.google.com/file/d/${getDriveFileId(msg.file_url)}/preview`} target="_blank" rel="noreferrer" style={styles.attachmentButton}>View</a>
                              <a href={`https://drive.google.com/uc?export=download&id=${getDriveFileId(msg.file_url)}`} target="_blank" rel="noreferrer" style={styles.attachmentButton}>Download</a>
                            </div>
                          </div>
                        )}

                        {msg.file_url === 'pending' && (
                          <div style={styles.pendingFile}>⏳ {msg.file_name} — uploading...</div>
                        )}
                      </>
                    )}
                  </div>

                  <div style={styles.messageMeta}>
                    <span style={styles.messageTime}>{formatTime(msg.created_at)}</span>
                    {readCount > 0 && <span style={styles.readCount}>Seen by {readCount}</span>}
                    {!isDeleted && (
                      <button style={styles.replyBtn} onClick={() => handleReply(msg)}>Reply</button>
                    )}
                    {isOwn && !isDeleted && isHovered && (
                      <button style={styles.unsendBtn} onClick={() => handleUnsend(msg.id)}>Unsend</button>
                    )}
                  </div>
                </div>
              );
            })}
          <div ref={messagesEndRef} />
        </div>

        <div style={styles.inputArea}>
          {error && <div style={styles.error}>{error}</div>}

          {replyingToMessage && (
            <div style={styles.replyComposer}>
              <div style={styles.replyComposerBody}>
                <div style={styles.replyComposerLabel}>
                  Replying to{' '}
                  {replyingToMessage.instructor_id === session.instructorId
                    ? 'yourself'
                    : getInstructorName(replyingToMessage.instructors)}
                </div>
                <div style={styles.replyComposerText}>{getMessagePreview(replyingToMessage)}</div>
              </div>
              <button style={styles.replyCancelBtn} onClick={() => setReplyToMessageId(null)}>×</button>
            </div>
          )}

          {showEmoji && (
            <div style={styles.emojiPanel}>
              {EMOJI_LIST.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  style={styles.emojiBtn}
                  onClick={() => setText(prev => prev + emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          <div style={styles.inputRow}>
            <button type="button" style={styles.iconBtn} onClick={() => setShowEmoji(p => !p)}>Emoji</button>
            <button
              type="button"
              style={styles.iconBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingFile || !activeChatId}
            >Attach</button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={e => handleFileUpload(e.target.files?.[0])}
            />
            <textarea
              ref={textInputRef}
              style={styles.textInput}
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={uploadingFile ? 'Uploading...' : 'Type a message... (Enter to send, Shift+Enter for new line)'}
              rows={1}
              disabled={uploadingFile}
            />
            <button
              style={styles.sendBtn}
              onClick={handleSend}
              disabled={sending || uploadingFile || !text.trim() || !activeChatId}
            >
              {sending ? '...' : '→'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap: {},
  heading: { margin: '0 0 20px', fontSize: '24px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px' },
  shell: {
    background: 'white',
    border: '1px solid #d7dee5',
    borderRadius: '12px',
    boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 160px)',
    overflow: 'hidden'
  },
  chatHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid #d7dee5',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  chatSelect: {
    flex: 1,
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '14px',
    fontWeight: '600',
    background: 'white',
    color: '#1f2933'
  },
  headerTools: { display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 },
  iconBtn: {
    minWidth: '54px', padding: '0 10px', border: '1px solid #d7dee5',
    background: 'white', borderRadius: '5px', height: '38px', fontSize: '11px',
    fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0
  },
  iconBtnActive: { background: '#eef5fa', borderColor: '#9fb3c8', color: '#215b7d' },
  searchBar: {
    display: 'flex', gap: '8px', alignItems: 'center',
    padding: '9px 16px', background: 'white', borderBottom: '1px solid #d7dee5'
  },
  searchInput: {
    flex: 1, minWidth: 0, border: '1px solid #bcccdc',
    borderRadius: '5px', padding: '8px 10px', fontSize: '13px', fontFamily: 'inherit'
  },
  searchClear: {
    width: '32px', height: '32px', border: '1px solid #d7dee5',
    background: 'white', color: '#66788a', borderRadius: '5px', cursor: 'pointer'
  },
  fileBrowser: {
    background: 'white', borderBottom: '1px solid #d7dee5',
    maxHeight: '280px', overflow: 'hidden', display: 'flex', flexDirection: 'column'
  },
  fileBrowserHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '11px 16px', borderBottom: '1px solid #edf1f5'
  },
  fileBrowserTitle: {
    fontSize: '12px', fontWeight: '800', color: '#1f2933',
    textTransform: 'uppercase', letterSpacing: '0.06em'
  },
  fileBrowserClose: { border: 'none', background: 'transparent', color: '#66788a', fontSize: '18px', cursor: 'pointer' },
  fileBrowserList: { overflowY: 'auto', padding: '4px 16px 10px' },
  fileBrowserItem: {
    display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto',
    gap: '12px', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #edf1f5'
  },
  fileBrowserName: {
    minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
    whiteSpace: 'nowrap', fontSize: '13px', fontWeight: '600', color: '#1f2933'
  },
  fileBrowserDate: { fontSize: '11px', color: '#829ab1', whiteSpace: 'nowrap' },
  fileBrowserActions: { display: 'flex', gap: '6px' },
  fileBrowserBtn: {
    border: '1px solid #bcccdc', background: 'white', color: '#215b7d',
    borderRadius: '4px', padding: '5px 8px', fontSize: '10px',
    fontWeight: '700', textDecoration: 'none'
  },
  fileBrowserEmpty: { padding: '16px 0', color: '#829ab1', fontSize: '13px' },
  messagesArea: {
    flex: 1, overflowY: 'auto', padding: '16px',
    display: 'flex', flexDirection: 'column', gap: '12px'
  },
  messageRow: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' },
  messageRowOwn: { alignItems: 'flex-end' },
  senderName: { fontSize: '11px', fontWeight: '700', color: '#66788a', marginBottom: '2px', paddingLeft: '4px' },
  bubble: { maxWidth: '75%', borderRadius: '10px', padding: '10px 14px', fontSize: '15px', lineHeight: '1.5', wordBreak: 'break-word' },
  bubbleOwn: { background: '#215b7d', color: 'white', borderBottomRightRadius: '4px' },
  bubbleOther: { background: '#f4f6f8', color: '#1f2933', border: '1px solid #d7dee5', borderBottomLeftRadius: '4px' },
  deletedText: { color: '#aaa', fontStyle: 'italic', fontSize: '13px' },
  messageText: { whiteSpace: 'pre-wrap' },
  messageImage: { maxWidth: '100%', maxHeight: '300px', borderRadius: '5px', display: 'block', cursor: 'pointer', marginBottom: '6px' },
  fileAttachment: { minWidth: '180px' },
  fileHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '7px' },
  fileIcon: { fontSize: '9px', fontWeight: '800', letterSpacing: '0.06em', flexShrink: 0 },
  fileName: { fontSize: '13px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  attachmentActions: { display: 'flex', gap: '5px', alignItems: 'center' },
  attachmentButton: {
    display: 'inline-block', padding: '2px 7px', borderRadius: '4px',
    border: '1px solid currentColor', color: 'inherit', background: 'rgba(255,255,255,0.12)',
    textDecoration: 'none', fontSize: '10px', fontWeight: '600', lineHeight: '16px', cursor: 'pointer'
  },
  pendingFile: { fontSize: '13px', color: '#627d98', fontStyle: 'italic', marginTop: '4px' },
  messageMeta: { display: 'flex', gap: '8px', alignItems: 'center', paddingLeft: '4px', paddingRight: '4px' },
  messageTime: { fontSize: '11px', color: '#aaa' },
  readCount: { fontSize: '11px', color: '#aaa' },
  replyBtn: { border: 'none', background: 'none', color: '#66788a', fontSize: '11px', cursor: 'pointer', padding: 0, textDecoration: 'underline' },
  unsendBtn: { border: 'none', background: 'none', color: '#a12a2a', fontSize: '11px', cursor: 'pointer', padding: 0, textDecoration: 'underline' },
  replyQuote: { borderLeft: '3px solid currentColor', borderRadius: '6px', background: 'rgba(127,127,127,0.12)', padding: '6px 8px', marginBottom: '7px' },
  replyQuoteAuthor: { fontSize: '11px', fontWeight: '700', opacity: 0.85, marginBottom: '2px' },
  replyQuoteText: { fontSize: '12px', lineHeight: '1.35', opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  inputArea: { flexShrink: 0, background: 'white', borderTop: '1px solid #d7dee5', padding: '12px 16px' },
  replyComposer: {
    display: 'flex', alignItems: 'stretch', gap: '8px',
    border: '1px solid #d7dee5', borderLeft: '4px solid #215b7d',
    borderRadius: '5px', background: '#f4f6f8', marginBottom: '8px', overflow: 'hidden'
  },
  replyComposerBody: { flex: 1, minWidth: 0, padding: '8px 10px' },
  replyComposerLabel: { fontSize: '11px', fontWeight: '700', color: '#215b7d', marginBottom: '2px' },
  replyComposerText: { fontSize: '12px', color: '#66788a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  replyCancelBtn: { width: '36px', border: 'none', background: 'transparent', color: '#66788a', fontSize: '22px', cursor: 'pointer', flexShrink: 0 },
  emojiPanel: { display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '8px', background: '#f4f6f8', borderRadius: '6px', border: '1px solid #d7dee5', marginBottom: '8px' },
  emojiBtn: { border: 'none', background: 'none', fontSize: '22px', cursor: 'pointer', padding: '4px', borderRadius: '6px', lineHeight: 1 },
  inputRow: { display: 'flex', gap: '8px', alignItems: 'flex-end' },
  textInput: {
    flex: 1, border: '1px solid #d7dee5', borderRadius: '6px', padding: '10px 14px',
    fontSize: '15px', resize: 'none', fontFamily: 'inherit', lineHeight: '1.4',
    maxHeight: '120px', overflowY: 'auto'
  },
  sendBtn: {
    width: '44px', background: '#215b7d', color: 'white', border: 'none',
    borderRadius: '5px', height: '38px', fontSize: '11px', fontWeight: '700',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  error: { background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '5px', padding: '8px 12px', color: '#a12a2a', fontSize: '13px', marginBottom: '8px' }
};